import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiChevronLeft, FiMessageSquare, FiMic } from 'react-icons/fi';
import {
  dossierErrorKind,
  getOpportunityDossier,
  linkDossierItem,
  unlinkDossierItem,
  type OpportunityDossier,
} from '../services/CrmDossierService';
import { getContactOrigin, type ContactOriginInfo } from '../services/CardService';
import { getMemory, type MemoryDto } from '../mendsAi/services/MendsVisionService';
import { listCrmAutomationLogs, type CrmAutomationLogEntry } from '../services/CrmAutomationService';
import { normalizeTemperature } from '../services/revenueTypes';
import { openRegisterConversation } from '../mendsAi/openRegister';
import { CommercialFieldList } from '../mendsAi/components/CommercialFieldList';
import TemperatureChip from '../mendsAi/components/revenue/TemperatureChip';
import { formatMoney } from '../components/crm/OpportunityPanel';
import LeadFormAnswers from '../components/crm/LeadFormAnswers';
import DossierDocuments from '../components/crm/dossier/DossierDocuments';
import DossierCommitments from '../components/crm/dossier/DossierCommitments';
import DossierNextAction from '../components/crm/dossier/DossierNextAction';
import { formatDossierWhen, logsForCard, nextCardCommitment } from '../components/crm/dossier/dossierFormat';
import { formatConfidence, moveAuthorLabel, moveRouteLabel } from '../utils/crmMoveLabels';
import './OpportunityDossierPage.css';

type LoadError = 'not_found' | 'forbidden' | 'other';
type Side<T> = { state: 'loading' } | { state: 'error' } | { state: 'ok'; value: T };

const ERROR_TEXT: Record<LoadError, string> = {
  not_found: 'Oportunidade não encontrada.',
  forbidden: 'Você não tem acesso a esta oportunidade.',
  other: 'Não deu para abrir a ficha agora.',
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');

const OpportunityDossierPage: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ cardId: string }>();
  const cardId = /^\d+$/.test(params.cardId ?? '') ? Number(params.cardId) : null;

  const [dossier, setDossier] = useState<OpportunityDossier | null>(null);
  const [error, setError] = useState<LoadError | null>(cardId == null ? 'not_found' : null);
  const [loading, setLoading] = useState(cardId != null);
  const [origin, setOrigin] = useState<Side<ContactOriginInfo | null>>({ state: 'loading' });
  const [memory, setMemory] = useState<Side<MemoryDto>>({ state: 'loading' });
  const [logs, setLogs] = useState<Side<CrmAutomationLogEntry[]>>({ state: 'loading' });
  const [busyItemId, setBusyItemId] = useState<string | null>(null);
  const [linkError, setLinkError] = useState(false);
  const requestRef = useRef(0);

  const loadDossier = useCallback(async (id: number): Promise<OpportunityDossier | null> => {
    const request = ++requestRef.current;
    try {
      const result = await getOpportunityDossier(id);
      if (request !== requestRef.current) return null;
      setDossier(result);
      setError(null);
      return result;
    } catch (e) {
      if (request !== requestRef.current) return null;
      setError(dossierErrorKind(e));
      return null;
    }
  }, []);

  const loadSide = useCallback((d: OpportunityDossier) => {
    const sectorId = d.card.sectorId;
    const contactId = d.contact.id;
    setOrigin({ state: 'loading' });
    setMemory({ state: 'loading' });
    setLogs({ state: 'loading' });
    getContactOrigin(sectorId, contactId).then(
      (value) => setOrigin({ state: 'ok', value }),
      () => setOrigin({ state: 'error' }),
    );
    getMemory(sectorId, contactId).then(
      (value) => setMemory({ state: 'ok', value }),
      () => setMemory({ state: 'error' }),
    );
    listCrmAutomationLogs(sectorId, contactId).then(
      (value) => setLogs({ state: 'ok', value }),
      () => setLogs({ state: 'error' }),
    );
  }, []);

  const loadAll = useCallback(
    async (id: number) => {
      setLoading(true);
      const d = await loadDossier(id);
      setLoading(false);
      if (d) loadSide(d);
    },
    [loadDossier, loadSide],
  );

  useEffect(() => {
    if (cardId == null) return;
    setDossier(null);
    loadAll(cardId);
  }, [cardId, loadAll]);

  const reload = useCallback(async (): Promise<boolean> => {
    if (cardId == null) return false;
    return (await loadDossier(cardId)) != null;
  }, [cardId, loadDossier]);

  const [reloadError, setReloadError] = useState(false);

  /** Vincula (ou desvincula) o item. `targetCardId` leva o vínculo para outra oportunidade do cliente (corrige a atribuição). */
  const changeLink = useCallback(
    async (itemId: string, action: 'link' | 'unlink', targetCardId?: number) => {
      if (cardId == null) return;
      setBusyItemId(itemId);
      setLinkError(false);
      setReloadError(false);
      try {
        await (action === 'link' ? linkDossierItem(targetCardId ?? cardId, itemId) : unlinkDossierItem(cardId, itemId));
      } catch {
        setLinkError(true);
        setBusyItemId(null);
        return;
      }
      if (!(await reload())) setReloadError(true);
      setBusyItemId(null);
    },
    [cardId, reload],
  );

  const nextCommitment = useMemo(() => (dossier ? nextCardCommitment(dossier.commitments.items) : null), [dossier]);
  const cardLogs = useMemo(() => {
    if (!dossier || logs.state !== 'ok') return [];
    return logsForCard(logs.value, dossier.card.id, dossier.otherOpportunities.length + 1);
  }, [dossier, logs]);

  if (error && !dossier) {
    return (
      <div className="dossier-page">
        <div className="dossier-state dossier-state--error">
          <p role="alert">{ERROR_TEXT[error]}</p>
          {error === 'other' && cardId != null && (
            <button type="button" className="dossier-btn" onClick={() => loadAll(cardId)}>
              Tentar de novo
            </button>
          )}
          <button type="button" className="dossier-btn" onClick={() => navigate('/crm')}>
            Voltar ao funil
          </button>
        </div>
      </div>
    );
  }
  if (loading || !dossier) {
    return (
      <div className="dossier-page">
        <p className="dossier-empty">Carregando a ficha…</p>
      </div>
    );
  }

  const { card, contact, people, stage, otherOpportunities, conversation, decisions } = dossier;
  // Fonte que falhou nunca vira "Nenhum": só afirma que não há compromisso quando todas as fontes carregaram.
  const nextCommitmentText = nextCommitment
    ? `${nextCommitment.title}, ${formatDossierWhen(nextCommitment.at)}`
    : dossier.commitments.status === 'unavailable'
      ? 'Indisponível'
      : dossier.commitments.status === 'partial'
        ? 'Nenhum nas fontes que carregaram'
        : 'Nenhum';
  const openPath = (path: string) => navigate(path);
  const openRegister = () =>
    openRegisterConversation({
      contactId: contact.id,
      contactName: contact.name,
      opportunityId: card.id,
      sourceSurface: 'oportunidade',
      screen: 'funnel',
    });
  const temperature = normalizeTemperature(card.temperature);

  return (
    <div className="dossier-page">
      <header className="dossier-header">
        <nav className="dossier-crumbs" aria-label="Caminho">
          <button type="button" className="dossier-crumb-back" onClick={() => navigate('/crm')}>
            <FiChevronLeft aria-hidden /> Funil
          </button>
          <span>/</span>
          <span>{card.columnName ?? 'Sem etapa'}</span>
          <span>/</span>
          <span>Oportunidade</span>
        </nav>
        <div className="dossier-header__row">
          <div className="dossier-header__text">
            <h1>{`${contact.name} · ${card.title ?? 'Oportunidade sem assunto'}`}</h1>
            <p className="dossier-subtitle">Um mesmo histórico para contato, conversa, oportunidade e compromissos.</p>
          </div>
          <div className="dossier-header__actions">
            <button type="button" className="dossier-btn" onClick={() => navigate(`/chat?contactId=${contact.id}`)}>
              <FiMessageSquare aria-hidden /> Ver conversa
            </button>
            <button type="button" className="dossier-btn dossier-btn--primary" onClick={openRegister}>
              <FiMic aria-hidden /> Registrar
            </button>
          </div>
        </div>
      </header>

      {linkError && (
        <p className="dossier-note dossier-note--warn" role="status">
          Não deu para vincular agora.
        </p>
      )}
      {reloadError && (
        <p className="dossier-note dossier-note--warn" role="status">
          Vinculado, mas não deu para atualizar a ficha agora.
        </p>
      )}

      <div className="dossier-layout">
        <div className="dossier-main">
          <section className="dossier-card dossier-sec dossier-sec--summary" aria-label="Resumo da oportunidade">
            <h3 className="dossier-card__title">Resumo da oportunidade</h3>
            <div className="dossier-tags">
              <span className="dossier-chip">{card.columnName ?? 'Sem etapa'}</span>
              <TemperatureChip temperature={temperature} />
              {card.isClosed && <span className="dossier-chip">Encerrada</span>}
            </div>
            <dl className="dossier-facts">
              <div>
                <dt>Valor estimado</dt>
                <dd>{formatMoney(card.estimatedValue, card.currency)}</dd>
              </div>
              <div>
                <dt>Etapa atual</dt>
                <dd>{card.columnName ?? 'Sem etapa'}</dd>
              </div>
              <div>
                <dt>Próximo compromisso</dt>
                <dd>{nextCommitmentText}</dd>
              </div>
            </dl>
          </section>

          <div className="dossier-sec dossier-sec--next">
            <DossierNextAction section={dossier.nextAction} onOpen={openPath} onRegister={openRegister} />
          </div>

          <section className="dossier-card dossier-sec dossier-sec--stage">
            <h3 className="dossier-card__title">Por que esta oportunidade está nesta etapa?</h3>
            {stage?.evidence ? (
              <>
                <p className="dossier-item__quote">{`“${stage.evidence}”`}</p>
                <p className="dossier-note">
                  {[
                    moveAuthorLabel({ triggerType: stage.trigger, actorName: stage.actorName }),
                    formatDossierWhen(stage.since),
                    formatConfidence(stage.confidence) ? `confiança ${formatConfidence(stage.confidence)}` : '',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </>
            ) : (
              <p className="dossier-empty">Sem evidência registrada para esta etapa.</p>
            )}
          </section>

          <div className="dossier-sec dossier-sec--commitments">
            <DossierCommitments
              section={dossier.commitments}
              contactName={contact.name}
              busyItemId={busyItemId}
              onLink={(id) => changeLink(id, 'link')}
              onUnlink={(id) => changeLink(id, 'unlink')}
              otherOpportunities={otherOpportunities}
              onMove={(id, target) => changeLink(id, 'link', target)}
              onOpen={openPath}
              onRetry={reload}
            />
          </div>

          <div className="dossier-sec dossier-sec--documents">
            <DossierDocuments
              section={dossier.documents}
              contactName={contact.name}
              busyItemId={busyItemId}
              onLink={(id) => changeLink(id, 'link')}
              onUnlink={(id) => changeLink(id, 'unlink')}
              otherOpportunities={otherOpportunities}
              onMove={(id, target) => changeLink(id, 'link', target)}
              onOpen={openPath}
              onRetry={reload}
            />
          </div>

          <section className="dossier-card dossier-sec dossier-sec--history">
            <h3 className="dossier-card__title">Histórico desta oportunidade</h3>
            {logs.state === 'loading' && <p className="dossier-empty">Carregando…</p>}
            {logs.state === 'error' && <p role="alert">Não deu para carregar o histórico.</p>}
            {logs.state === 'ok' &&
              (cardLogs.length === 0 ? (
                <p className="dossier-empty">Nenhuma movimentação registrada ainda.</p>
              ) : (
                <ul className="dossier-list">
                  {cardLogs.map((l) => (
                    <li key={l.id} className="dossier-item">
                      <div className="dossier-item__body">
                        <strong className="dossier-item__title">{moveRouteLabel(l) || l.columnName || 'Movimentação'}</strong>
                        <span className="dossier-item__meta">
                          {[moveAuthorLabel(l), formatDossierWhen(l.createdAt)].filter(Boolean).join(' · ')}
                        </span>
                        {l.detail && <span className="dossier-item__summary">{l.detail}</span>}
                        {l.evidence && <span className="dossier-item__quote">{`“${l.evidence}”`}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              ))}
          </section>
        </div>

        <aside className="dossier-side">
          <section className="dossier-card dossier-sec dossier-sec--client">
            <h3 className="dossier-card__title">Cliente e contexto</h3>
            <div className="dossier-client">
              <span className="dossier-avatar" aria-hidden>{initials(contact.name)}</span>
              <strong>{contact.name}</strong>
            </div>
            <h4 className="dossier-group__title">Memória do cliente (todas as oportunidades)</h4>
            {memory.state === 'loading' && <p className="dossier-empty">Carregando…</p>}
            {memory.state === 'error' && <p role="alert">Não deu para carregar a memória.</p>}
            {memory.state === 'ok' && (
              <>
                <p>{memory.value.summary?.trim() ? memory.value.summary : 'Sem memória registrada.'}</p>
                <CommercialFieldList properties={memory.value.properties} history={memory.value.history} />
              </>
            )}
            <dl className="dossier-pairs">
              <div>
                <dt>Origem original</dt>
                <dd>{origin.state === 'ok' ? origin.value?.label ?? 'Não identificada' : origin.state === 'error' ? 'Indisponível' : '…'}</dd>
              </div>
              <div>
                <dt>Canal atual</dt>
                <dd>{origin.state === 'ok' ? origin.value?.currentChannel ?? 'WhatsApp' : '…'}</dd>
              </div>
              <div>
                <dt>Funcionário digital</dt>
                <dd>{people.aiAgentName ?? 'Nenhum'}</dd>
              </div>
              <div>
                <dt>Setor</dt>
                <dd>{people.sectorName ?? '—'}</dd>
              </div>
              <div>
                <dt>Responsável humano</dt>
                <dd>{people.responsibleName ?? 'Sem responsável'}</dd>
              </div>
            </dl>
            <LeadFormAnswers sectorId={card.sectorId} contactId={contact.id} variant="dossier" />
          </section>

          <section className="dossier-card dossier-sec dossier-sec--conversation">
            <h3 className="dossier-card__title">Conversa</h3>
            {conversation.status === 'unavailable' ? (
              <p role="alert">Não deu para carregar a conversa.</p>
            ) : (
              <>
                <p>
                  {conversation.lastInboundAt
                    ? `Última mensagem do cliente: ${formatDossierWhen(conversation.lastInboundAt)}`
                    : 'O cliente ainda não escreveu.'}
                </p>
                {conversation.lastInboundPreview && <p className="dossier-item__quote">{`“${conversation.lastInboundPreview}”`}</p>}
                <p className="dossier-note">{`${conversation.whatsappMessages} mensagens no WhatsApp`}</p>
                {conversation.emailMessages > 0 && <p className="dossier-note">{`${conversation.emailMessages} e-mails`}</p>}
                <button type="button" className="dossier-btn" onClick={() => openPath(conversation.openPath)}>
                  Abrir conversa
                </button>
              </>
            )}
          </section>

          <section className="dossier-card dossier-sec dossier-sec--others">
            <h3 className="dossier-card__title">Outras oportunidades deste cliente</h3>
            {otherOpportunities.length === 0 ? (
              <p className="dossier-empty">Este cliente não tem outras oportunidades.</p>
            ) : (
              <ul className="dossier-list">
                {otherOpportunities.map((o) => (
                  <li key={o.id}>
                    <button type="button" className="dossier-other" onClick={() => navigate(`/crm/oportunidade/${o.id}`)}>
                      <strong>{o.title ?? 'Oportunidade sem assunto'}</strong>
                      <span>{[o.columnName, o.isClosed ? 'Encerrada' : null].filter(Boolean).join(' · ')}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="dossier-card dossier-sec dossier-sec--decisions">
            <h3 className="dossier-card__title">Decisões pendentes</h3>
            {decisions.status === 'unavailable' ? (
              <p role="alert">Não deu para carregar as decisões.</p>
            ) : decisions.items.length === 0 ? (
              <p className="dossier-empty">Nenhuma aprovação pendente.</p>
            ) : (
              <ul className="dossier-list">
                {decisions.items.map((d) => (
                  <li key={d.id} className="dossier-item">
                    <div className="dossier-item__body">
                      <strong className="dossier-item__title">{d.title}</strong>
                      <span className="dossier-item__meta">{formatDossierWhen(d.createdAt)}</span>
                      <div className="dossier-item__actions">
                        <button type="button" className="dossier-btn" onClick={() => openPath(d.openPath)}>
                          Abrir na Central
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
};

export default OpportunityDossierPage;
